import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import PlusIcon from '@/assets/icons/plus.svg?react';
import {
  useDeleteMultipleClients,
  useFindClients,
} from '@/hooks/useAgencyClient';
import { useForm, useWatch } from 'react-hook-form';
import { LabelValue } from '@/types/global';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import { PAGE_SIZE } from '@/constants/pageSize';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Loader from '@/components/ui/Loader';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledInput from '@/components/styled/StyledInput';
import StyledTable from '@/components/styled/StyledTable';
import { getClientsColumnDef } from '@/types/clients/clientsColumnDef';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import SearchIcon from '@/assets/icons/search.svg?react';
import { ClientsResponse } from '@/api/client.api';
import { RowSelectionState, SortingState } from '@tanstack/react-table';
import { PATHS } from '@/constants/Paths';
import { useNavigate } from 'react-router';
import ModalDeletion from '@/components/ui/ModalDeletion';
import { useToast } from '@/hooks/useToast';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

type TabsOptions = 'all' | 'mine';

interface ClientsFilter {
  search: string;
  agent?: LabelValue<string>;
}

const ClientsPage = () => {
  const navigate = useNavigate();
  const { isTourOperator, tLabel, tPages } = useDistributorLabels();

  const { control } = useForm<ClientsFilter>({
    mode: 'onChange',
    defaultValues: {
      agent: undefined,
      search: '',
    },
  });

  const agent = useWatch({ control, name: 'agent' });
  const search = useWatch({ control, name: 'search' });
  const debouncedSearch = useDebouncedSearch(search);

  const [currTab, setCurrTab] = useState<TabsOptions>('all');

  const filters = useMemo(
    () => ({ agentId: agent?.value ?? '', name: debouncedSearch }),
    [agent, debouncedSearch],
  );

  const { page, setPage, setSorting, sortField, sortOrder, sorting } =
    useTableControls();

  const {
    data: clientsResponse,
    isSuccess,
    isLoading,
  } = useFindClients(
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
      onlyMine: currTab === 'mine',
    },
    (data) => ({
      ...data,
      agents: data.agents.map(({ firstname, id, lastname }) => ({
        value: id,
        label: `${firstname} ${lastname}`,
      })),
    }),
  );

  const { toastAsync } = useToast();
  const { mutateAsync: deleteMultipleClients } = useDeleteMultipleClients();
  const [clientsToDeleteIds, setClientsToDeleteIds] = useState<string[]>([]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectedClientIds = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  return (
    <StyledContainer>
      <StyledListHeader title={tLabel('title')} subTitle={tLabel('subTitle')}>
        <StyledButton
          title={tLabel('delete')}
          onClick={() => {
            setClientsToDeleteIds(selectedClientIds);
          }}
          className="font-semibold gap-1 px-3 py-2"
          variant="outlined"
          disabled={selectedClientIds.length === 0}
        />
        <StyledButton
          title={tLabel('newClient')}
          onClick={() => navigate(PATHS.distributors.clients.create)}
          className="px-3 py-2"
          icon={<PlusIcon className="size-5 fill-white" />}
        />
      </StyledListHeader>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <Tabs
          defaultValue={currTab}
          value={currTab}
          onValueChange={(value) => setCurrTab(value as TabsOptions)}
          className="w-full"
        >
          <TabsList>
            <TabsTrigger
              value="all"
              className="hover:cursor-pointer flex items-center justify-center gap-2"
            >
              <span>{tPages('all')}</span>
              {isSuccess && clientsResponse.total > 0 && (
                <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                  {clientsResponse.total}
                </div>
              )}
            </TabsTrigger>
            {isSuccess && clientsResponse.totalMine > 0 && (
              <TabsTrigger
                value="mine"
                className="hover:cursor-pointer flex items-center justify-center gap-2"
              >
                <span>{tPages('myClients')}</span>
                <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                  {clientsResponse.totalMine}
                </div>
              </TabsTrigger>
            )}
          </TabsList>
          <div className="flex flex-col gap-6 w-full h-auto pt-4">
            <TabsContent value="all">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-normal text-gray-600">
                  {tPages('filterBy')}
                </span>
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <StyledSelect
                      className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                      control={control}
                      options={clientsResponse?.agents ?? []}
                      name="agent"
                      placeholder={tPages('agent')}
                      isClearable={true}
                    />
                  </div>
                  <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
                    <StyledInput
                      control={control}
                      name="search"
                      placeholder={tPages('quickSearch')}
                      icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
                    />
                  </div>
                </div>
                {isLoading ? (
                  <Loader />
                ) : (
                  <ClientsList
                    isTourOperator={isTourOperator}
                    clientsResponse={clientsResponse}
                    page={page}
                    setPage={setPage}
                    sorting={sorting}
                    setSorting={setSorting}
                    isSuccess={isSuccess}
                    onDeleteClient={(clientId) => {
                      setClientsToDeleteIds([clientId]);
                    }}
                    setRowSelection={setRowSelection}
                  />
                )}
              </div>
            </TabsContent>
            <TabsContent value="mine">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-normal text-gray-600">
                  {tPages('filterBy')}
                </span>
                <div className="flex flex-col md:flex-row-reverse gap-4">
                  <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
                    <StyledInput
                      control={control}
                      name="search"
                      placeholder={tPages('quickSearch')}
                      icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
                    />
                  </div>
                </div>
                {isLoading ? (
                  <Loader />
                ) : (
                  <ClientsList
                    isTourOperator={isTourOperator}
                    clientsResponse={clientsResponse}
                    page={page}
                    setPage={setPage}
                    sorting={sorting}
                    setSorting={setSorting}
                    isSuccess={isSuccess}
                    onDeleteClient={(clientId) => {
                      setClientsToDeleteIds([clientId]);
                    }}
                    setRowSelection={setRowSelection}
                  />
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
      {clientsToDeleteIds.length > 0 && (
        <ModalDeletion
          actionOnClick={() => {
            toastAsync(deleteMultipleClients(clientsToDeleteIds), {
              success: tPages('clientsDeletedSuccess'),
            });
          }}
          closeModal={() => {
            setClientsToDeleteIds([]);
          }}
          type="client"
          isMultiple={clientsToDeleteIds.length > 1}
        />
      )}
    </StyledContainer>
  );
};

export default ClientsPage;

interface ClientsListProps {
  isTourOperator: boolean;
  clientsResponse?: Omit<ClientsResponse, 'agents'> & {
    agents: LabelValue<string>[];
  };
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isSuccess: boolean;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  onDeleteClient: (clientId: string) => void;
}

const ClientsList = ({
  isTourOperator,
  clientsResponse,
  page,
  setPage,
  sorting,
  setSorting,
  isSuccess,
  setRowSelection,
  onDeleteClient,
}: ClientsListProps) => {
  const [, setSelection] = useState<RowSelectionState>({});
  const navigate = useNavigate();
  const getTranslatedConst = useTranslatedConst();
  const columnDef = useCallback(
    (t: Parameters<typeof getClientsColumnDef>[0]) =>
      getClientsColumnDef(t, isTourOperator),
    [isTourOperator],
  );
  const navigateToClient = useCallback(
    (clientId: string) => {
      navigate(PATHS.distributors.clients.view.replace(':clientId', clientId));
    },
    [navigate],
  );

  return (
    <>
      <StyledTable
        columns={getTranslatedConst(columnDef)}
        data={clientsResponse?.clients ?? []}
        sorting={sorting}
        setSorting={setSorting}
        getRowId={(row) => row.id}
        onViewRow={(row) => navigateToClient(row.original.id)}
        onRowClick={(row) => navigateToClient(row.original.id)}
        onDeleteRow={(row) => {
          onDeleteClient(row.original.id);
        }}
        setSelection={(selection) => {
          setSelection(selection);
          setRowSelection(selection);
        }}
      />
      {isSuccess && (
        <StyledNextPreviousButtons
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          dataLength={clientsResponse?.total ?? 0}
          label={isTourOperator ? 'agence' : 'client'}
        />
      )}
    </>
  );
};
